import React, { useState, useEffect } from "react";
import { aboutApi } from "../../services/api";
import { useToast } from "./Toast";

const AboutPage: React.FC = () => {
  const { showToast } = useToast();
  const [bioPt, setBioPt] = useState("");
  const [bioEn, setBioEn] = useState("");
  const [activeTab, setActiveTab] = useState<"pt" | "en">("pt");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBio = async () => {
    setLoading(true);
    try {
      const response = await aboutApi.getBio();
      if (response.success && response.data) {
        setBioPt(response.data.pt);
        setBioEn(response.data.en);
      } else {
        showToast(response.message || "Failed to load bio content", "error");
      }
    } catch {
      showToast("Failed to load bio content", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await aboutApi.updateBio({ pt: bioPt, en: bioEn });
      if (response.success) {
        showToast(response.message || "Bio updated successfully");
      } else {
        showToast(response.message || "Failed to save", "error");
      }
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const currentBio = activeTab === "pt" ? bioPt : bioEn;
  const setCurrentBio = activeTab === "pt" ? setBioPt : setBioEn;

  const insertTag = (tag: string) => {
    const textarea = document.getElementById("bio-editor") as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = currentBio.substring(start, end);
    const newText = `<${tag}>${selectedText}</${tag}>`;

    const updatedBio = currentBio.substring(0, start) + newText + currentBio.substring(end);
    setCurrentBio(updatedBio);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL:");
    if (!url) return;

    const textarea = document.getElementById("bio-editor") as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = currentBio.substring(start, end) || "Link text";
    const linkHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${selectedText}</a>`;

    const updatedBio = currentBio.substring(0, start) + linkHtml + currentBio.substring(end);
    setCurrentBio(updatedBio);
  };

  const insertBreak = () => {
    const textarea = document.getElementById("bio-editor") as HTMLTextAreaElement;
    if (!textarea) return;

    const pos = textarea.selectionStart;
    const updatedBio = currentBio.substring(0, pos) + "<br><br>" + currentBio.substring(pos);
    setCurrentBio(updatedBio);
  };

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">About / Bio</h1>
        <p className="admin-page-subtitle">
          Edit your bio with HTML — see a live preview on the right
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-tabs">
          <button
            className={`admin-tab${activeTab === "pt" ? " active" : ""}`}
            onClick={() => setActiveTab("pt")}
          >
            Portuguese (PT)
          </button>
          <button
            className={`admin-tab${activeTab === "en" ? " active" : ""}`}
            onClick={() => setActiveTab("en")}
          >
            English (EN)
          </button>
        </div>

        {/* Toolbar */}
        <div className="admin-btn-group" style={{ marginBottom: "1rem" }}>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => insertTag("strong")}
            title="Bold"
          >
            <i className="fa-solid fa-bold" />
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => insertTag("em")}
            title="Italic"
          >
            <i className="fa-solid fa-italic" />
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={insertLink}
            title="Insert Link"
          >
            <i className="fa-solid fa-link" />
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={insertBreak}
            title="Line Break"
          >
            <i className="fa-solid fa-arrow-turn-down" />
            <span>br</span>
          </button>
        </div>

        <div className="admin-editor-container">
          <div className="admin-editor-pane">
            <span className="admin-editor-pane-label">
              HTML Editor ({activeTab.toUpperCase()})
            </span>
            <textarea
              id="bio-editor"
              className="admin-editor-textarea"
              value={currentBio}
              onChange={(e) => setCurrentBio(e.target.value)}
              spellCheck={false}
            />
          </div>
          <div className="admin-editor-pane">
            <span className="admin-editor-pane-label">Live Preview</span>
            <div
              className="admin-preview-pane"
              dangerouslySetInnerHTML={{ __html: currentBio }}
            />
          </div>
        </div>

        <div className="admin-btn-group" style={{ marginTop: "2rem" }}>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
            <span>{saving ? "Saving..." : "Save Bio"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
